import React from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Eye, Factory, Play, RefreshCw } from 'lucide-react';

import { DEPT_NAMES } from '@/lib/constants';
import { PRIORITY_MAP } from '@/lib/status-colors';
import { INPUT_CLASS, inputStyle } from '@/components/phone/FieldControls';
import {
  BottomSheet, EmptyState, IconButton, PageHeader, PhonePage, PrimaryButton, ScreenLoader, SearchBar, StatusPill,
} from '@/components/phone/primitives';
import { Colors, pcs, priorityTone } from '@/components/phone/theme';

/**
 * A machine man's queue, on the phone he actually carries.
 *
 * The desktop shows a seven-column grid with a specification rail; a man at a
 * press wants the next job, its stage, and two buttons. Each card is one batch
 * standing at his machine — tap the body to open the specification, Start to
 * begin the run, Complete to close the stage. Closing opens a sheet at the
 * thumb with the same three numbers the desktop strip asks for.
 */

function Spec({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-medium uppercase tracking-[0.5px]" style={{ color: Colors.text.secondary }}>
        {label}
      </p>
      <p className="mt-0.5 break-words text-[13px] font-semibold" style={{ color: Colors.text.primary }}>{value}</p>
    </div>
  );
}

function QueueCard({ item, open, onToggle, onStart, onComplete, onOpenOrder }) {
  const running = item.status === 'in_progress';
  const specs = Object.entries(item.specifications || {}).filter(([, v]) => v && v !== 'None' && v !== 'No');
  return (
    <li
      data-testid={`queue-card-${item.batch_id}`}
      className="mt-3 rounded-[10px] border"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`queue-toggle-${item.batch_id}`}
        aria-expanded={open}
        className="flex w-full items-start gap-2 px-4 pt-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
            {item.item_name || item.product_subtype || DEPT_NAMES[item.order_type] || 'Item'}
          </span>
          <span className="mt-0.5 block truncate text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
            {item.customer_name}{item.firm_name ? ` — ${item.firm_name}` : ''}
          </span>
          <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
            {item.order_id} · {pcs(item.quantity)}
          </span>
        </span>
        {open
          ? <ChevronUp className="mt-1 h-[18px] w-[18px] shrink-0" style={{ color: Colors.text.tertiary }} />
          : <ChevronDown className="mt-1 h-[18px] w-[18px] shrink-0" style={{ color: Colors.text.tertiary }} />}
      </button>

      <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pt-2">
        <StatusPill label={item.current_stage_name} tone="accent" dot />
        <StatusPill
          label={running ? 'Running' : 'Waiting'}
          tone={running ? 'positive' : 'caution'}
          testID={`queue-status-${item.batch_id}`}
        />
        {item.priority && item.priority !== 'medium' && (
          <StatusPill label={PRIORITY_MAP[item.priority]?.label || item.priority} tone={priorityTone(item.priority)} />
        )}
      </div>

      {open && (
        <div className="border-t px-4 py-3" style={{ borderColor: Colors.border.subtle }} data-testid={`queue-specs-${item.batch_id}`}>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            <Spec label="Order" value={item.order_id} />
            <Spec label="Stage" value={item.current_stage_name} />
            {specs.map(([k, v]) => <Spec key={k} label={k.replace(/_/g, ' ')} value={String(v)} />)}
          </div>
          {!!item.special_instructions && (
            <p
              className="mt-3 rounded-[10px] px-3 py-2 text-[12px] leading-snug"
              style={{ backgroundColor: Colors.semantic.cautionWash, color: Colors.semantic.caution }}
            >
              {item.special_instructions}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 border-t px-3 py-2" style={{ borderColor: Colors.border.subtle }}>
        {running ? (
          <button
            type="button"
            data-testid={`complete-${item.batch_id}`}
            onClick={onComplete}
            className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-[10px] text-[13px] font-bold"
            style={{ backgroundColor: Colors.semantic.positive, color: Colors.text.inverse }}
          >
            <CheckCircle2 className="h-4 w-4" /> Complete
          </button>
        ) : (
          <button
            type="button"
            data-testid={`start-${item.batch_id}`}
            onClick={onStart}
            className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-[10px] text-[13px] font-bold"
            style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
          >
            <Play className="h-4 w-4" /> Start
          </button>
        )}
        <button
          type="button"
          onClick={onOpenOrder}
          aria-label="Open order"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border"
          style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
        >
          <Eye className="h-[18px] w-[18px]" style={{ color: Colors.accent.DEFAULT }} />
        </button>
      </div>
    </li>
  );
}

function NumberInput({ label, name, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-medium" style={{ color: Colors.text.secondary }}>{label}</span>
      <input
        data-testid={`field-${name}`}
        className={INPUT_CLASS}
        style={inputStyle}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
        onFocus={(e) => e.target.select()}
        inputMode="numeric"
        placeholder="0"
        aria-label={label}
      />
    </label>
  );
}

export default function PhoneQueue({
  items, loading, userName, onSearch, onRefresh,
  expandedId, onToggle, onStart, onOpenOrder,
  completing, form, onFormChange, onOpenComplete, onCancelComplete, onSubmitComplete, submitting,
}) {
  const running = items.filter((i) => i.status === 'in_progress').length;
  const done = parseInt(form.quantity_completed, 10) || 0;
  const total = completing?.totalQty || 0;
  const short = done > 0 && done < total;

  return (
    <PhonePage testID="my-production-queue-page">
      <PageHeader
        title="My Queue"
        subtitle={userName}
        action={<IconButton icon={RefreshCw} onClick={onRefresh} label="Refresh" testID="refresh-queue-btn" />}
      />

      <div className="px-5">
        <SearchBar placeholder="Order, customer…" onChange={onSearch} testID="queue-search-input" />
        <p className="mt-3 text-[12px] font-medium" style={{ color: Colors.text.secondary }} data-testid="queue-counts">
          {items.length} job{items.length === 1 ? '' : 's'} · {running} running · {items.length - running} waiting
        </p>
      </div>

      <div className="flex-1 px-5 pb-8">
        {loading ? (
          <ScreenLoader />
        ) : items.length === 0 ? (
          <EmptyState icon={Factory} message="Nothing queued for you right now" />
        ) : (
          <ul data-testid="queue-list">
            {items.map((item) => (
              <QueueCard
                key={item.batch_id}
                item={item}
                open={expandedId === item.batch_id}
                onToggle={() => onToggle(item)}
                onStart={() => onStart(item)}
                onComplete={() => onOpenComplete(item)}
                onOpenOrder={() => onOpenOrder(item)}
              />
            ))}
          </ul>
        )}
      </div>

      <BottomSheet
        open={!!completing}
        onClose={onCancelComplete}
        title={completing ? `Close ${completing.item?.current_stage_name}` : ''}
        subtitle={completing ? `${completing.item?.item_name || completing.item?.order_id} · ${pcs(total)}` : ''}
        testID="complete-sheet"
      >
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Done" name="quantity_completed" value={form.quantity_completed}
            onChange={(v) => onFormChange('quantity_completed', v)} />
          <NumberInput label="Wastage" name="wastage" value={form.wastage}
            onChange={(v) => onFormChange('wastage', v)} />
        </div>
        <label className="mt-3 block">
          <span className="mb-1.5 block text-[11px] font-medium" style={{ color: Colors.text.secondary }}>Remarks</span>
          <input
            data-testid="field-remarks"
            className={INPUT_CLASS}
            style={inputStyle}
            value={form.remarks}
            onChange={(e) => onFormChange('remarks', e.target.value)}
            placeholder="Optional"
            aria-label="Remarks"
          />
        </label>
        {short && (
          <p className="mt-3 text-[12px]" style={{ color: Colors.semantic.caution }} data-testid="split-warning">
            {done} of {total} — the remaining {total - done} stays at this stage in a new batch.
          </p>
        )}
        <PrimaryButton className="mt-4" onClick={onSubmitComplete} busy={submitting} testID="confirm-complete-btn">
          Done
        </PrimaryButton>
      </BottomSheet>
    </PhonePage>
  );
}
